<template>
  <div class="clp-content wide main-content">
    <div
      v-if="backgroundUrl !== ''"
      class="clp-banner"
      :style="$_ImageHelpers_getBackgroundImage(backgroundUrl)"
    />

    <div class="clp-lead-block">
      <count-down-block
        v-if="deadline"
        :deadline="deadline"
        :title="timerTitle"
        :type="'gray'"
      />

      <div
        v-if="introText"
        class="intro-text-block wrap"
      >
        <heading :level="2">{{ pageTitle }}</heading>
        <div
          v-dompurify-html="introText"
          class="text"
        />
      </div>

      <div
        v-if="flightDetails.book.title || flightDetails.travel.title"
        class="flight-details"
      >
        <div class="flight-details-item">
          <div class="flight-details-title">{{ flightDetails.book.title }}</div>
          <div class="flight-details-date">{{ flightDetails.book.firstLine }}</div>
          <div
            v-if="!isMobile && flightDetails.book.secondLine"
            class="flight-details-additional"
          >
            {{ flightDetails.book.secondLine }}
          </div>
        </div>

        <div class="flight-details-item">
          <div class="flight-details-title">{{ flightDetails.travel.title }}</div>
          <div class="flight-details-date">{{ flightDetails.travel.firstLine }}</div>
          <div
            v-if="!isMobile && flightDetails.travel.secondLine"
            class="flight-details-additional"
          >
            {{ flightDetails.travel.secondLine }}
          </div>
        </div>
      </div>
    </div>

    <div class="clp-main-block">
      <div
        v-if="saleDescriptionItems && saleDescriptionItems.length"
        class="sale-container"
      >
        <div class="wrap">
          <div
            v-for="(saleDescriptionItem, index) in saleDescriptionItems"
            :key="index"
            class="sale-description-item"
          >
            <h3 v-if="saleDescriptionItem.title">{{ saleDescriptionItem.title }}</h3>
            <div
              v-if="saleDescriptionItem.description"
              v-dompurify-html="saleDescriptionItem.description"
              class="sale-description"
            />
            <div
              v-if="
                saleDescriptionItem.blueButtonLink && saleDescriptionItem.blueButtonText
              "
              :class="[
                'btn',
                'btn-blue',
                { center: saleDescriptionItem.blueButtonInCenter }
              ]"
            >
              <a :href="saleDescriptionItem.blueButtonLink">
                {{ saleDescriptionItem.blueButtonText }}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="Object.keys(origins).length"
        class="flights-block"
      >
        <div class="flights-filter">
          <div class="wrap">
            <div class="dropdown-group">
              <div class="label-wrap">
                <label>{{ $t('campaignLandingPage.filter.origin') }}:</label>
              </div>
              <auto-complete
                class="select"
                :placeholder="$t('searchForm.originDestin.origin.placeholderEHorizon')"
                :selected-item="selectedOrigin"
                :mobile-title="$t('campaignLandingPage.filter.selectOrigin')"
                :items="originItems"
                :starts-with="true"
                :clear-on-focus="true"
                :need-destination-filter="true"
                @select="originSelected"
              />
            </div>

            <div class="dropdown-group">
              <div class="label-wrap">
                <label>{{ $t('campaignLandingPage.filter.orderBy') }}:</label>
              </div>
              <dropdown-simple
                class="select"
                :translation-prefix="'reusable.sorters.'"
                :items="sorterItems"
                :selected-item="selectedSorter"
                :device-type="deviceType"
                :mobile-title="$t('campaignLandingPage.filter.selectOrderBy')"
                @select="selectSorter"
              />
            </div>
          </div>
        </div>

        <div class="wrap">
          <div
            v-if="!isMobile"
            class="col-title"
          >
            <div class="col-1">
              <div class="destination">{{ $t('campaignLandingPage.destination') }}</div>
            </div>
            <div
              v-if="hasPrices"
              class="col-2"
            >
              <div
                v-if="!isMobile"
                class="price"
              >
                {{ $t('campaignLandingPage.flightsStartPrice') }}
              </div>
            </div>
          </div>

          <div
            v-if="selectedOrigin && origins[selectedOrigin]"
            class="flights-list"
          >
            <div
              v-for="(destination, index) in origins[selectedOrigin].destinations"
              :key="index"
              class="flight-row"
            >
              <div :class="['flight-row-inner', { 'no-price': !hasPrices }]">
                <div class="col-1">
                  <div class="destination-wrap">
                    <div>
                      <span class="destination">{{ destination.city }}</span>
                      <span
                        v-if="destination.comment && !isMobile"
                        class="new-flight"
                      >
                        {{ destination.comment }}
                      </span>
                      <div
                        v-if="destination.timesPerWeek"
                        class="non-stop-flight"
                      >
                        ({{ destination.timesPerWeek }}
                        {{ $t('campaignLandingPage.timesPerWeek') }})
                      </div>
                    </div>
                    <div
                      v-if="showDates"
                      class="date"
                    >
                      <span v-if="getStartDate(destination.startDate) !== ''">
                        {{ $t('campaignLandingPage.flightsStartDate') }}:
                      </span>
                      {{ getStartDate(destination.startDate) }}
                    </div>
                    <span
                      v-if="destination.comment && isMobile"
                      class="new-flight"
                    >
                      {{ destination.comment }}
                    </span>
                  </div>
                </div>

                <div
                  v-if="hasPrices"
                  class="col-2"
                >
                  <div class="price-wrap">
                    <div>
                      <span
                        v-if="isMobile"
                        class="price-from"
                      >
                        {{ $t('campaignLandingPage.flightsStartPrice') }}:
                      </span>
                      <span class="value">
                        {{ destination.price }}
                        {{ destination.currency }}
                      </span>
                      <span
                        v-if="destination.cherry"
                        class="cherry"
                      >
                        <img
                          :src="getDestinIcon(destination.cherry)"
                          alt=""
                          width="17"
                          height="17"
                        />
                      </span>
                      <span
                        v-if="destination.fltType === 1"
                        class="trip-type"
                      >
                        {{ $t('searchForm.radio.oneWay') }}
                      </span>
                      <span
                        v-if="destination.fltType === 2"
                        class="trip-type"
                      >
                        {{ $t('searchForm.radio.roundTrip') }}
                      </span>
                    </div>
                  </div>
                </div>

                <div
                  v-if="!isMobile"
                  class="btn btn-blue"
                >
                  <a
                    v-if="!enableNuxtFSF"
                    :href="getDeepLink(destination, origins[selectedOrigin].type)"
                  >
                    {{ $t('campaignLandingPage.findFlightsButton') }}
                  </a>
                  <a
                    v-else
                    :href="getNuxtDeepLink(destination, origins[selectedOrigin].type)"
                  >
                    {{ $t('campaignLandingPage.findFlightsButton') }}
                  </a>
                </div>
              </div>
              <div
                v-if="isMobile"
                class="btn btn-blue"
              >
                <a :href="getDeepLink(destination, origins[selectedOrigin].type)">
                  {{ $t('campaignLandingPage.findFlightsButton') }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="bottom-block">
        <div class="wrap">
          <terms-and-conditions
            v-if="termsAndConditions.content !== ''"
            :title="termsAndConditions.title"
            :content="termsAndConditions.content"
          />

          <div
            v-if="noteText !== ''"
            v-dompurify-html="noteText"
            class="please-note-block"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import moment from 'moment'
  import conf from 'conf'

  import CherryIcon from 'assets/images/clp/cherry.svg'
  import PumpkinIcon from 'assets/images/clp/pumpkin.png'
  import XmasIcon from 'assets/images/clp/xmas.svg'
  import SunnyIcon from 'assets/images/clp/sunny.png'
  import WinterIcon from 'assets/images/clp/winter.png'
  import ImageHelpers from 'utils/mixins/ImageHelpers'

  import isUndefined from 'lodash-es/isUndefined'

  export default {
    components: {
      'count-down-block': () =>
        import(
          /* webpackChunkName: "count-down-block" */ 'containers/Pages/LandingPage/CountDownBlock'
        ),
      'dropdown-simple': () =>
        import(
          /* webpackChunkName: "dropdown-simple" */ 'components/Dropdown/DropdownSimple'
        ),
      'terms-and-conditions': () =>
        import(
          /* webpackChunkName: "terms-and-conditions" */ 'components/TermsAndConditions'
        ),
      'auto-complete': () =>
        import(
          /* webpackChunkName: "autocomplete-dropdown" */ 'components/Dropdown/Autocomplete'
        ),
      heading: () => import(/* webpackChunkName: "heading" */ 'components/Heading')
    },
    mixins: [ImageHelpers],
    data() {
      return {
        originItems: []
      }
    },
    computed: {
      ...mapState('device', ['deviceType']),
      ...mapGetters('device', ['isMobile']),

      ...mapState('campaignLandingPage', [
        'bannerDesktop',
        'bannerTablet',
        'bannerMobile',
        'pageTitle',
        'introText',
        'origins',
        'hasPrices',
        'hasDates',
        'flightDetails',
        'timerTitle',
        'deadline',
        'selectedOrigin',
        'selectedSorter',
        'defaultFlightType',
        'saleDescriptionItems',
        'termsAndConditionsVisible',
        'termsAndConditions',
        'noteText',
        'iconType'
      ]),

      ...mapState('main', ['selectedLang', 'enableNuxtFSF']),
      showDates() {
        return this.hasDates.includes(this.selectedOrigin)
      },
      backgroundUrl() {
        return this.$_ImageHelpers_responsiveImage({
          desktop: this.bannerDesktop,
          tablet: this.bannerTablet,
          mobile: this.bannerMobile
        })
      },

      sorterItems() {
        const items = [
          {
            value: 'destinationAsc',
            title: this.$t('reusable.sorters.destinationAsc')
          },
          {
            value: 'destinationDesc',
            title: this.$t('reusable.sorters.destinationDesc')
          }
        ]

        if (this.hasPrices) {
          items.push({
            value: 'priceAsc',
            title: this.$t('reusable.sorters.priceAsc')
          })

          items.push({
            value: 'priceDesc',
            title: this.$t('reusable.sorters.priceDesc')
          })
        } else if (
          this.selectedSorter === 'priceAsc' ||
          this.selectedSorter === 'priceDesc'
        ) {
          this.$store.commit('campaignLandingPage/setSelectedSorter', 'destinationAsc')
        }

        return items
      },

      defaultCherryIcon() {
        switch (this.iconType) {
          default:
          case 'cherry':
            return CherryIcon
          case 'pumpkin':
            return PumpkinIcon
          case 'xmas':
            return XmasIcon
        }
      }
    },
    created() {
      this.storeOrigins()
    },

    methods: {
      storeOrigins() {
        this.originItems = Object.keys(this.origins).map((key) => ({
          value: key,
          city: this.origins[`${key}`].city,
          country: this.origins[`${key}`].country,
          code: key,
          title: `${this.origins[`${key}`].city} (${key}) - ${
            this.origins[`${key}`].country
          }`
        }))

        this.originItems = this.sortAlphabetically(this.originItems, 'city')
      },

      originSelected(value) {
        if (!value) {
          return
        }

        this.$store.dispatch('campaignLandingPage/setSelectedOrigin', value)
      },

      sortAlphabetically(data, key) {
        return data.sort((a, b) => {
          if (a['city'] === b['city']) {
            return a['value'].localeCompare(b['value'])
          }

          if (!isUndefined(a[`${key}`] && !isUndefined(b[`${key}`]))) {
            return a[`${key}`].localeCompare(b[`${key}`])
          }

          return false
        })
      },

      getDeepLink(item, originType) {
        const startDate = this.formatStartDateForDeeplink(item.startDate)
        const deepLinkType =
          startDate !== '' && this.getStartDateDiff(item.startDate) > 0
            ? 'calendar'
            : 'histogram'

        return (
          conf.newIbeUrl +
          '/' +
          this.selectedLang +
          '/fb/' +
          deepLinkType +
          '?' +
          'originCode=' +
          this.selectedOrigin +
          '&destinCode=' +
          item.code +
          '&tripType=' +
          (item.fltType === 1 ? 'oneway' : 'return') +
          '&originType=' +
          originType +
          '&destinType=' +
          item.type +
          '&numAdt=1' +
          startDate +
          (item.voucher ? '&evoucher=' + item.voucher : '')
        )
      },
      getNuxtDeepLink(item, originType) {
        const startDate = this.formatStartDateForNuxtDeeplink(item.startDate)

        let url = new URL(`${conf.nuxtUrl}${this.$t('searchForm.bookFlightUrl')}`)
        let params = new URLSearchParams(url.search)

        params.append('tripType', item.fltType === 1 ? 'oneway' : 'return')
        params.append('originCode', this.selectedOrigin)
        params.append('originType', originType)
        params.append('destinCode', item.code)
        params.append('destinType', item.type)
        params.append('numAdt', 1)
        params.append('numChd', 0)
        params.append('numInf', 0)
        params.append('numYth', 0)

        params.append('selectedMonth', startDate)

        if (item.voucher) {
          params.append('evoucher', item.voucher)
        }

        return `${url.toString()}?${params.toString()}`
      },

      formatStartDateForDeeplink(date) {
        const diff = this.getStartDateDiff(date)
        return diff < 0 ? `&selectedMonth=0` : `&selectedMonth=${diff}`
      },

      formatStartDateForNuxtDeeplink(date) {
        const diff = this.getStartDateDiff(date)
        return diff < 0 ? 0 : diff
      },

      getStartDateDiff(date) {
        if (!date) {
          return ''
        }
        date = moment(date, 'DD.MM.YYYY')
        return date.month() - moment().month()
      },

      getStartDate(date) {
        const isDateInPast = moment().isAfter(moment(date, 'DD.MM.YYYY'))
        return !isDateInPast ? date || '' : ''
      },

      selectSorter(sortBy) {
        this.$store.dispatch('campaignLandingPage/setSelectedSorter', sortBy)
      },

      getDestinIcon(icon) {
        switch (icon) {
          case 'sunny':
            return SunnyIcon
          case 'winter':
            return WinterIcon
          case 1:
          default:
            return this.defaultCherryIcon
        }
      }
    }
  }
</script>

<style lang="scss" scoped rel="stylesheet/scss">
  @import '~assets/styles/common.scss';

  .clp-banner {
    width: 100%;
    height: 200px;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center center;

    @include bp-tablet-only {
      height: 325px;
    }

    @include bp-mf-desktop {
      height: 350px;
      background-size: auto;
    }
  }

  ::v-deep .countdown-block {
    @include bp-mf-tablet {
      margin-top: -71px;
    }
  }

  .clp-lead-block {
    text-align: center;
    padding-bottom: 27px;

    @include bp-mf-tablet {
      padding-bottom: 40px;
    }
  }

  .intro-text-block {
    padding: 27px 23px 0;
    text-align: center;

    .text {
      position: relative;
      margin: 0 auto;
      font-size: em(16);
      line-height: 24px;
      font-weight: 300;

      &:after {
        content: '';
        position: absolute;
        right: 0;
        bottom: -10px;
        left: 0;
        margin: 0 auto;
        width: 40px;
        height: 3px;
        background-color: $green-base;
      }

      ::v-deep .js-inline-button {
        font-size: rem(15, 16);
      }
    }

    ::v-deep strong {
      font-weight: 500;
    }

    ::v-deep a:not(.js-inline-button) {
      color: $blue-medium;

      &:hover {
        @include bp-mf-tablet {
          color: $blue-light;
        }
      }
    }
  }

  .flight-details {
    margin: 0 auto;
    padding-top: 26px;
    max-width: 740px;

    @include bp-mf-tablet {
      padding: 26px 16px 0;
    }
  }

  .flight-details-item {
    padding: 0 15px;
    overflow: hidden;

    @include bp-mf-tablet {
      display: inline-block;
      vertical-align: top;
      width: 49%;
    }

    &:first-child {
      margin-bottom: 5px;

      @include bp-mf-tablet {
        margin-bottom: 0;
        border-right: 1px solid $grey-border;
        text-align: right;
      }
    }

    &:last-child {
      @include bp-mf-tablet {
        text-align: left;
      }
    }
  }

  .flight-details-title {
    font-weight: 500;
    line-height: 34px;
  }

  .flight-details-date {
    font-size: em(16);
    line-height: 16px;
  }

  .flight-details-additional {
    margin-top: 8px;
    white-space: inherit;
    line-height: 16px;
    color: $grey;
  }

  .sale-container {
    background-color: $formBlock;

    .wrap {
      padding: 27px 23px;
      max-width: 740px;

      @include bp-tablet-only {
        padding: 44px 26px;
      }

      @include bp-mf-tablet {
        padding: 44px 0;
      }
    }
  }

  .sale-description-item {
    &:not(:first-child) {
      margin-top: 40px;
    }

    h3 {
      margin: 0 0 13px;
      font-size: em(20);
      line-height: 24px;
      font-weight: 500;

      @include bp-mf-tablet {
        margin-bottom: 20px;
      }

      + .btn-blue {
        margin-top: 10px;
      }
    }

    ::v-deep p {
      margin: 0;
      font-size: em(14);
      line-height: 20px;

      @include bp-mf-tablet {
        font-size: em(16);
        line-height: 24px;
      }

      &:not(:last-child) {
        margin-bottom: 13px;
      }

      + ul {
        margin-top: -10px;
      }
    }

    ::v-deep strong {
      font-weight: 500;
    }

    ::v-deep a {
      color: $blue-medium;

      &:hover {
        @include bp-mf-tablet {
          color: $blue-light;
        }
      }
    }

    ::v-deep ul {
      list-style: none;
      margin-bottom: 15px;

      @include bp-mf-tablet {
        margin-bottom: 18px;
      }
    }

    ::v-deep li {
      position: relative;
      padding: 3px 0 3px 30px;
      font-size: em(14);
      line-height: 21px;

      @include bp-mf-tablet {
        padding: 4px 0 4px 31px;
        font-size: em(16);
        line-height: 24px;
      }

      &:before {
        content: '';
        display: block;
        position: absolute;
        top: 11px;
        left: 3px;
        width: 8px;
        height: 8px;
        background-color: $green-base;
        border-radius: 50%;

        @include bp-mf-tablet {
          top: 12px;
          left: 0;
        }
      }

      img {
        position: absolute;
        left: 0;
        width: 20px;
        background-color: $formBlock;

        @include bp-mf-tablet {
          width: 25px;
        }
      }
    }

    .sale-description {
      + .btn-blue,
      + .btn-blue.center {
        margin-top: 20px;

        @include bp-mf-tablet {
          margin-top: 30px;
        }
      }
    }

    .btn-blue {
      max-width: 100%;
      width: 250px;
      height: 48px;

      @include bp-mf-tablet {
        height: 52px;
      }

      &.center {
        display: block;
        margin: auto;
      }

      a {
        padding: 0 16px;
        line-height: 48px;
        color: #ffffff;

        @include bp-mf-tablet {
          line-height: 52px;
        }
      }
    }
  }

  .flights-block {
    @include bp-mf-tablet {
      padding-bottom: 44px;
      background-color: $gray-light-background;
    }

    > .wrap {
      @include bp-tablet-only {
        padding: 0 26px;
      }
    }
  }

  .flights-filter {
    padding: 20px 23px 25px;
    background-color: #dfe2ea;

    @include bp-mf-tablet {
      padding: 40px 24px;
      text-align: center;
    }

    .wrap {
      display: flex;
      justify-content: center;

      @include bp-mobile {
        flex-direction: column;
      }
    }

    .dropdown-wrap {
      ::v-deep {
        .arrow-down:after {
          content: $icon-arrow-right-thin-medium;
          color: $blue-medium;
          top: 21px;
          right: 15px;
        }

        input.dropdown-field {
          color: $grey-black-lighter;
        }
      }
    }
  }

  .label-wrap {
    margin-bottom: 9px;
    font-size: em(12);
    line-height: 14px;

    @include bp-mf-tablet {
      font-size: em(14);
      line-height: 16px;
    }
  }

  .dropdown-group {
    @include bp-mf-tablet {
      display: inline-block;
      width: 300px;
      text-align: left;
    }

    &:first-child {
      margin-bottom: 16px;

      @include bp-mf-tablet {
        margin: 0 20px 0 0;
      }
    }

    .dropdown-wrap {
      display: block;
    }

    ::v-deep .dropdown-field {
      height: 48px;
      @include text-overflow();
      line-height: 48px;
      font-size: em(16);

      @include bp-mf-tablet {
        height: 52px;
        line-height: 52px;
      }

      &:after {
        content: $icon-arrow-right-thin-medium;
        transform: rotate(90deg);
        top: inherit;
        right: 16px;
        font-size: 11px;
        line-height: 48px;
        color: $blue-medium;

        @include bp-mf-tablet {
          top: inherit;
          line-height: 52px;
        }
      }
    }

    ::v-deep .dropdown-list {
      @include bp-mf-tablet {
        top: 52px;
        height: auto;
        max-height: 193px;
      }

      li {
        display: flex;
        align-items: center;
        height: 48px;
        line-height: inherit;
      }
    }
  }

  .col-title {
    margin-top: 30px;
    font-weight: 500;
    line-height: 46px;

    @include bp-tablet-only {
      padding: 0 178px 0 16px;
    }

    @include bp-mf-desktop {
      padding: 0 193px 0 24px;
    }

    div {
      display: inline-block;
    }
  }

  .flight-row {
    border-bottom: 1px solid $grey-border;

    @include bp-mf-tablet {
      border-bottom: 0;
    }

    &:not(:last-child) {
      @include bp-mf-tablet {
        margin-bottom: 12px;
      }
    }

    .btn-blue a {
      line-height: 48px;
    }

    .btn-blue {
      margin: 0 24px 15px;
      min-width: calc(100% - 46px);
    }
  }

  .flight-row-inner {
    position: relative;
    padding: 16px 23px 80px;
    min-height: 75px;
    background-color: white;

    @include bp-tablet-only {
      padding: 0 178px 0 16px;
    }

    @include bp-mf-tablet {
      height: 75px;
      min-height: inherit;
      border: 1px solid $grey-border;
      border-radius: 6px;
      border-bottom: 1px solid $grey-border;
    }

    @include bp-mf-desktop {
      padding: 0 193px 0 24px;
      line-height: 75px;
    }

    &.no-price {
      padding-bottom: 0;
    }

    &.opened {
      @include bp-mf-tablet {
        border-radius: 6px 6px 0 0;
        min-height: inherit;
      }
    }

    .btn-blue {
      position: absolute;
      right: 23px;
      bottom: 20px;
      width: 130px;
      min-width: inherit;
      height: 48px;

      @include bp-tablet-only {
        right: 16px;
      }

      @include bp-mf-tablet {
        top: 13px;
        bottom: inherit;
        width: 140px;
      }

      @include bp-mf-desktop {
        right: 24px;
      }

      button {
        padding: 0 16px;
        font-size: em(14);
        line-height: 48px;
      }
    }
  }

  .col-1,
  .col-2 {
    @include bp-tablet-only {
      vertical-align: top;
    }

    @include bp-mf-tablet {
      display: inline-block;
    }
  }

  .col-1 {
    @include bp-tablet-only {
      padding-right: 10%;
      width: calc(64% - 4px);
    }

    @include bp-mf-tablet {
      position: relative;
    }

    @include bp-mf-desktop {
      padding-right: 23%;
      width: calc(70% - 4px);
    }

    .date {
      @include bp-mf-desktop {
        position: absolute;
        top: 0;
        right: 0;
        width: 30%;
      }
    }
  }

  .col-2 {
    @include bp-tablet-only {
      width: 36%;
    }

    @include bp-mf-desktop {
      width: 30%;
    }
  }

  .destination-wrap {
    @include bp-tablet-only {
      display: flex;
      justify-content: center;
      flex-direction: column;
      height: 75px;
    }

    .destination {
      font-size: em(20);
      font-weight: 500;

      @include bp-mf-tablet {
        font-size: em(22);
      }
    }

    .non-stop-flight {
      display: inline-block;
      color: $grey;
    }

    .new-flight {
      display: inline-block;
      position: relative;
      top: -4px;
      padding: 2px 5px 1px;
      background: $green-base;
      border-radius: 3px;
      font-size: em(11);
      line-height: normal;
      text-transform: uppercase;
      color: $blue-dark;

      @include bp-mf-tablet {
        margin: 0 5px;
      }
    }

    .date {
      margin: 2px 0 10px;
      font-size: em(12);
      font-weight: 700;
      color: $grey;

      @include bp-mf-tablet {
        margin-bottom: 0;
        font-size: em(13);
      }

      @include bp-mf-desktop {
        margin: 0;
        width: 30%;
      }
    }
  }

  .price-wrap {
    position: absolute;
    bottom: 20px;
    left: 23px;
    padding-right: 140px;
    width: calc(100% - 46px);
    height: 48px;
    display: flex;
    justify-content: center;
    flex-direction: column;

    @include bp-mf-tablet {
      position: inherit;
      padding-right: 0;
      width: auto;
      height: 75px;
    }

    @include bp-mf-desktop {
      display: block;
    }
  }

  .price-from {
    display: block;
    font-size: em(11);
  }

  .value {
    font-size: em(20);
    font-weight: 700;

    @include bp-mf-desktop {
      font-size: em(22);
    }
  }

  .cherry {
    vertical-align: middle;
  }

  .trip-type {
    white-space: nowrap;
    font-size: em(11);
    color: $grey;

    @include bp-mf-desktop {
      font-size: em(14);
    }
  }

  .bottom-block {
    padding: 26px 0;
    text-align: center;

    @include bp-mf-tablet {
      padding: 44px 0;
    }

    .wrap {
      padding: 0 23px;

      @include bp-tablet-only {
        padding: 0 26px;
      }
    }

    .terms-and-conditions-block {
      ::v-deep .terms-and-conditions-panel-content {
        padding-left: 54px;
      }
    }

    ::v-deep .btn-print {
      position: inherit;
      margin: 26px auto 0;

      @media print {
        display: none;
      }

      @include bp-mf-tablet {
        margin-top: 44px;
      }
    }
  }

  .terms-and-conditions-block,
  .please-note-block {
    margin: 0 auto;
    max-width: 740px;
    text-align: left;

    ::v-deep p {
      margin: 0;
    }
  }

  .please-note-block {
    margin: 0 auto;
    padding: 26px 0;
    border-bottom: 1px solid $grey-border;
    font-size: em(12);
    line-height: 18px;

    @include bp-mf-tablet {
      padding: 44px 0;
      font-size: em(14);
      line-height: 21px;
    }

    ::v-deep a {
      color: $blue-medium;

      &:hover {
        @include bp-mf-tablet {
          color: $blue-light;
        }
      }
    }
  }
</style>
